/**
 * Rotas do painel `/admin` — só existem na build WEB.
 *
 * O painel é operado pelo navegador, nunca pelo app das lojas, e o bundle
 * nativo viaja DENTRO do app: cada chunk dele é peso de download. Por isso o
 * `AppRoutes` só chama `adminRoutes()` fora da build nativa
 * (`VITE_NATIVE_BUILD`, ligada só pelo `build:mobile`), e o Rollup descarta
 * este módulo inteiro — rotas, seções e `navigation.ts`.
 *
 * ⚠️ Os `lazy()` moram DENTRO de `adminRoutes()`, não no topo do módulo, e é
 * isso que faz o descarte funcionar: uma chamada no topo é efeito colateral
 * para o Rollup, que a mantém — e com ela o `import()` e o chunk — mesmo com
 * `adminRoutes` sem uso. O cache garante um `lazy()` por seção no processo
 * (um novo a cada render remontaria o painel). Quem cobra que nada do admin
 * sobrou no bundle nativo é o `scripts/mobile-postbuild.mjs`, pelos sourcemaps.
 *
 * Admin — FORA do DefaultPage de propósito: é a exceção ao guardrail da coluna
 * central (desktop-first, largura plena, tabelas densas). O guard de claim
 * `admin` vive no AdminShell; o gate real é server-side (`assertAdmin` em
 * cada callable).
 */

import { lazy, type ReactElement } from "react";
import { Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { OnboardingGuard } from "./OnboardingGuard";
import { RedirectKeepingSearch } from "./RedirectKeepingSearch";
import { adminTabsFor, ADMIN_DEFAULT_DESTINATION } from "../pages/admin/navigation";

let cached: ReactElement | null = null;

export function adminRoutes(): ReactElement {
  if (cached) return cached;

  const AdminShell      = lazy(() => import("../pages/admin/AdminShell"));
  const AdminDestination = lazy(() => import("../pages/admin/components/AdminDestination"));
  const AdminReports    = lazy(() => import("../pages/admin/sections/ReportsSection"));
  const AdminCreators   = lazy(() => import("../pages/admin/sections/CreatorsSection"));
  const AdminAuthorApplications = lazy(() => import("../pages/admin/sections/AuthorApplicationsSection"));
  const AdminReleases = lazy(() => import("../pages/admin/sections/ReleasesSection"));
  const AdminCuration   = lazy(() => import("../pages/admin/sections/CurationSection"));
  const AdminTemas      = lazy(() => import("../pages/admin/sections/ThemesSection"));
  const AdminAfiliados  = lazy(() => import("../pages/admin/sections/AffiliatesSection"));
  const AdminRuntime    = lazy(() => import("../pages/admin/sections/RuntimeSection"));
  const AdminConquistas = lazy(() => import("../pages/admin/sections/AdminConquistas"));
  const AdminStore      = lazy(() => import("../pages/admin/sections/StoreSection"));
  const AdminCampaigns  = lazy(() => import("../pages/admin/sections/CampaignsSection"));
  const AdminHealth     = lazy(() => import("../pages/admin/sections/HealthSection"));
  const AdminComunidade = lazy(() => import("../pages/admin/sections/ManualBooksSection"));
  const AdminBeta       = lazy(() => import("../pages/admin/sections/BetaSection"));
  const AdminAccounts   = lazy(() => import("../pages/admin/sections/AccountsSection"));
  const AdminDenuncias  = lazy(() => import("../pages/admin/sections/ContentReportsSection"));
  const AdminProAccess  = lazy(() => import("../pages/admin/sections/ProAccessSection"));
  const AdminAvisos     = lazy(() => import("../pages/admin/sections/AnnouncementsSection"));
  const AdminFeedback   = lazy(() => import("../pages/admin/sections/FeedbackSection"));
  const AdminSupport    = lazy(() => import("../pages/admin/sections/SupportSection"));
  const AdminPulse      = lazy(() => import("../pages/admin/sections/PulseSection"));
  const AdminInbox      = lazy(() => import("../pages/admin/sections/InboxSection"));
  const AdminEconomy    = lazy(() => import("../pages/admin/sections/EconomySection"));
  const AdminLeaguesWeek     = lazy(() => import("../pages/admin/sections/leagues/WeekTab"));
  const AdminLeaguesRooms    = lazy(() => import("../pages/admin/sections/leagues/RoomsTab"));
  const AdminLeaguesSeason   = lazy(() => import("../pages/admin/sections/leagues/SeasonTab"));
  const AdminLeaguesLedger   = lazy(() => import("../pages/admin/sections/leagues/XpLedgerTab"));
  const AdminLeaguesLive     = lazy(() => import("../pages/admin/sections/leagues/SeasonLiveTab"));

  cached = (
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <AdminShell />
            </OnboardingGuard>
          </ProtectedRoute>
        }
      >
        {/* ── 5 DESTINOS (03/09) ────────────────────────────────────
            Eram 10 abas agrupadas por assunto; agora são 5 agrupadas por
            RITMO, cada uma com sub-abas em rota aninhada (deep link e
            voltar preservados). O Pulso nasceu por último, junto do
            agregado diário (Onda 1), e por isso `/admin` deixou de abrir
            a Entrada: enquanto não havia série para mostrar, uma home
            vazia era pior que home nenhuma.

            As seções NÃO foram reescritas: cada uma continua sendo o mesmo
            componente, agora montado sob o destino a que pertence. */}
        <Route index element={<Navigate to={ADMIN_DEFAULT_DESTINATION} replace />} />

        {/* Pulso — a home. Lê do agregado diário; não computa nada. */}
        <Route path="pulso" element={<AdminDestination tabs={adminTabsFor("pulso")} />}>
          <Route index element={<Navigate to="hoje" replace />} />
          <Route path="hoje" element={<AdminPulse />} />
          <Route path="economia" element={<AdminEconomy />} />
        </Route>

        {/* Entrada — as três filas de voz do usuário. Antes viviam em três
            lugares diferentes, e a de feedback era a TERCEIRA sub-aba de
            uma seção sobre beta fechado. */}
        <Route path="entrada" element={<AdminDestination tabs={adminTabsFor("entrada")} />}>
          <Route index element={<Navigate to="tudo" replace />} />
          {/* A lista unificada: fan-in das três filas, rajada colapsada. */}
          <Route path="tudo" element={<AdminInbox />} />
          {/* Conversas de suporte (30/09) — docs/systems/support-threads.md */}
          <Route path="conversas" element={<AdminSupport />} />
          <Route path="denuncias" element={<AdminDenuncias />} />
          <Route path="catalogo"  element={<AdminReports />} />
          <Route path="feedback"  element={<AdminFeedback />} />
        </Route>

        {/* Pessoas — a conta e os dois acessos que se concedem A ELA. */}
        <Route path="pessoas" element={<AdminDestination tabs={adminTabsFor("pessoas")} />}>
          <Route index element={<Navigate to="contas" replace />} />
          <Route path="contas"   element={<AdminAccounts />} />
          <Route path="pro"      element={<AdminProAccess />} />
          <Route path="creators" element={<AdminCreators />} />
          <Route path="autores"  element={<AdminAuthorApplications />} />
        </Route>

        {/* Catálogo — editar e auditar o mesmo acervo. */}
        <Route path="catalogo" element={<AdminDestination tabs={adminTabsFor("catalogo")} />}>
          <Route index element={<Navigate to="curadoria" replace />} />
          <Route path="curadoria" element={<AdminCuration />} />
          <Route path="comunidade" element={<AdminComunidade />} />
          <Route path="lancamentos" element={<AdminReleases />} />
          <Route path="saude"     element={<AdminHealth />} />
        </Route>

        {/* Alavancas — o episódico. O que sobrou de "Beta" é o kill-switch
            do gate (desligado desde 01/09), e interruptor não merece um
            destino de navegação. */}
        <Route path="alavancas" element={<AdminDestination tabs={adminTabsFor("alavancas")} />}>
          <Route index element={<Navigate to="temas" replace />} />
          <Route path="temas"     element={<AdminTemas />} />
          <Route path="loja"      element={<AdminStore />} />
          <Route path="afiliados" element={<AdminAfiliados />} />
          <Route path="campanhas" element={<AdminCampaigns />} />
          <Route path="avisos"    element={<AdminAvisos />} />
          <Route path="beta"      element={<AdminBeta />} />
          <Route path="interruptores" element={<AdminRuntime />} />
          <Route path="conquistas" element={<AdminConquistas />} />
        </Route>

        {/* Ligas — Bloco B, Admin-Alavancas (14/09): moderação de sala
            privada + leitura de semana/temporada. Blueprint:
            docs/systems/admin-league-rooms.md. */}
        <Route path="ligas" element={<AdminDestination tabs={adminTabsFor("ligas")} />}>
          <Route index element={<Navigate to="semana" replace />} />
          <Route path="semana" element={<AdminLeaguesWeek />} />
          <Route path="salas" element={<AdminLeaguesRooms />} />
          <Route path="temporada" element={<AdminLeaguesSeason />} />
          <Route path="escada" element={<AdminLeaguesLive />} />
          <Route path="extrato" element={<AdminLeaguesLedger />} />
        </Route>

        {/* ── URLs antigas ─────────────────────────────────────────────
            As 10 rotas de antes seguem respondendo. `RedirectKeepingSearch`
            onde a query carrega informação: `?edit=<canonicalId>` é o
            atalho da fila de Reports pro editor de meta, e um `Navigate`
            cru o descartaria em silêncio. */}
        <Route path="reports"   element={<RedirectKeepingSearch to="/admin/entrada/catalogo" />} />
        <Route path="denuncias" element={<Navigate to="/admin/entrada/denuncias" replace />} />
        <Route path="accounts"  element={<RedirectKeepingSearch to="/admin/pessoas/contas" />} />
        <Route path="pro"       element={<Navigate to="/admin/pessoas/pro" replace />} />
        <Route path="creators"  element={<Navigate to="/admin/pessoas/creators" replace />} />
        <Route path="curadoria" element={<RedirectKeepingSearch to="/admin/catalogo/curadoria" />} />
        <Route path="health"    element={<Navigate to="/admin/catalogo/saude" replace />} />
        <Route path="campanhas" element={<Navigate to="/admin/alavancas/campanhas" replace />} />
        <Route path="avisos"    element={<Navigate to="/admin/alavancas/avisos" replace />} />
        <Route path="beta"      element={<Navigate to="/admin/alavancas/beta" replace />} />
        <Route path="catalog"   element={<RedirectKeepingSearch to="/admin/catalogo/curadoria" />} />
        <Route path="carousels" element={<RedirectKeepingSearch to="/admin/catalogo/curadoria" />} />
        <Route path="catalog-health" element={<Navigate to="/admin/catalogo/saude" replace />} />
      </Route>
  );
  return cached;
}
